import type { HelpSource } from './api';

/**
 * Conversations, as the server keeps them.
 *
 * They used to live in localStorage, which meant they did not follow an
 * operator from the console to the dashboard panel or to another machine.
 * They are now rows the server owns (help_threads / help_thread_messages),
 * scoped to one operator, and the client sends ONE message plus a thread id —
 * the server decides how much history the model sees.
 *
 * What is left here is the shaping the panel needs and nothing else.
 */
export interface HelpThread {
  id: string;
  title: string;
  /** Where the conversation started; the panel warns once the operator moves. */
  route: string | null;
  screen: string | null;
  /** 'waiting' or 'with_staff' while LimoSphere support is in this conversation. */
  handoffStatus?: 'bot' | 'waiting' | 'with_staff';
  createdAt: string;
  updatedAt: string;
}

/** A screenshot on a question. `url` is presigned and refreshed on every read. */
export interface HelpAttachment {
  key: string;
  fileName: string;
  contentType: string;
  fileSize: number | null;
  url: string | null;
}

export interface HelpMessage {
  id: string;
  /**
   * `staff` is a LimoSphere person answering; `system` is the desk's one-line
   * notice ("someone has joined") — read by the operator, never by the model.
   */
  role: 'user' | 'assistant' | 'staff' | 'system';
  content: string;
  attachments: HelpAttachment[];
  sources: HelpSource[];
  /**
   * Show the support card under this one. Server-set: from the escalate tool
   * the model CALLED, or on the notice that nobody picked a request up — so it
   * survives reopening the thread rather than being client state.
   */
  unresolved?: boolean;
  /** On a person's turn: the desk's name, never who typed it. */
  author?: string;
  createdAt: string;
}

const STORED_ID = /^\d+$/;

/** Numeric order for id strings, without trusting them to fit a double. */
const compareIds = (a: string, b: string) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);

/**
 * Merge messages into a transcript: each id once, in id (= commit) order, the
 * optimistic `pending-*` turns last. The tail poll and the answer to a send
 * can both carry the same row, and either can land first.
 */
export function mergeMessages(current: HelpMessage[], incoming: HelpMessage[]): HelpMessage[] {
  const byId = new Map<string, HelpMessage>();
  for (const message of [...current, ...incoming]) byId.set(message.id, message);
  const all = [...byId.values()];
  const stored = all.filter((message) => STORED_ID.test(message.id)).sort((a, b) => compareIds(a.id, b.id));
  return [...stored, ...all.filter((message) => !STORED_ID.test(message.id))];
}

/** The newest stored message id — where a tail poll picks up. */
export function lastStoredId(messages: HelpMessage[]): string {
  let last = '0';
  for (const message of messages) {
    if (STORED_ID.test(message.id) && compareIds(message.id, last) > 0) last = message.id;
  }
  return last;
}

/**
 * An optimistic user turn, shown before the server has answered. The screenshots
 * render from local object urls until the answer brings back the stored copy.
 */
export const pendingMessage = (content: string, attachments: HelpAttachment[] = []): HelpMessage => ({
  id: `pending-${Date.now()}`,
  role: 'user',
  content,
  attachments,
  sources: [],
  createdAt: new Date().toISOString(),
});

/** "2m ago", "3h ago", "yesterday" — enough to recognise a conversation. */
export function relativeTime(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}
